/* eslint-disable react/jsx-props-no-spreading */
import { configure } from 'mobx';
import { ThemeProvider } from 'theme-ui';
import { ReactElement, ReactNode, useEffect, useLayoutEffect, Profiler } from 'react';
import { SessionProvider } from 'next-auth/react';
import { appWithTranslation } from 'next-i18next';
import { DefaultSeo } from 'next-seo';
import Script from 'next/script';
import Head from 'next/head';
import { NextPage } from 'next/types';
import { useRouter } from 'next/router';
import theme from '@styles/theme';
import GlobalStyles from '@styles/theme/GlobalStyles';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import Layout from '@features/layout/components/Layout/Layout';
import { useRouteProgress } from '@hooks/useRouteProgress';
import logger from '@utils/logger';
import { BASE_PATH, REACT_PROFILING } from '@config/index';
import ReaffiliatesHead from '@reaffiliates/ReaffiliatesHead';
import DevToolbar from '@components/DevToolbar/DevToolbar';
import { isDevToolbarAllowed } from '@components/DevToolbar/helpers/isDevToolbarAllowed';
import prepareUrlToShare from '@utils/prepareUrlToShare';
import isMobileDevice from '@utils/isMobileDevice';
import { installScrollDebugPatches, isScrollDebugEnabled, logScrollTrigger } from '@utils/scrollDebug';
import { migratePanelTrueQueryToSessionFlag } from '@utils/openUserPanelAfterAuth';
import '@styles/external/nprogress.css';
import '@adyen/adyen-web/dist/adyen.css';
import '@styles/skip-links.css';
import '@features/productPreview/components/TrustMateReviews/omnibus-icons-global.css';
import '@styles/external/nprogress.css';
import '@adyen/adyen-web/dist/adyen.css';
import '@styles/skip-links.css';
import '@features/productPreview/components/TrustMateReviews/omnibus-icons-global.css';
import DefaultSeoConfig from '../../next-seo.config';
import nextI18NextConfig from '../../next-i18next.config';
import type { AppProps } from 'next/app';
configure({
  enforceActions: 'always'
});
type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement, pageProps: AppProps) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  // Fix React 19 type compatibility - Component can return ReactNode which includes undefined
  Component: NextPageWithLayout & React.ComponentType<Record<string, unknown>>;
};
const App = ({
  Component,
  pageProps
}: AppPropsWithLayout) => {
  const router = useRouter();
  useRouteProgress();
  useLayoutEffect(() => {
    migratePanelTrueQueryToSessionFlag();
  }, []);
  const isDevToolbarActive = () => {
    if (!isDevToolbarAllowed()) return false;
    const {
      devtoolbar
    } = router.query;
    return devtoolbar && devtoolbar === 'active';
  };
  const getLayout = (page: ReactElement) => Component.getLayout ? Component.getLayout(page, pageProps) : <Layout footer={pageProps.footer} navigationBar={pageProps.navigationBar} categoriesTree={pageProps.categoriesTree} promotionalMessages={pageProps.promotionalMessages} simpleLayout={pageProps.simpleLayout} overflowY={pageProps?.layoutOverflowY} overflowX={pageProps?.layoutOverflowX} attachFirstChildToNavigation={pageProps.attachFirstChildToNavigation} navParams={pageProps.navParams} addresses={pageProps.addresses}>
        {page}
      </Layout>;
  useEffect(() => {
    if (history.scrollRestoration !== 'manual') {
      history.scrollRestoration = 'manual';
    }
    installScrollDebugPatches();
    if (!isScrollDebugEnabled()) return undefined;
    const onRouteChangeStart = (url: string) => {
      logScrollTrigger('next:routeChangeStart', {
        url
      });
    };
    const onRouteChangeComplete = (url: string) => {
      logScrollTrigger('next:routeChangeComplete', {
        url
      });
    };
    const onPopState = () => {
      logScrollTrigger('window:popstate', {});
    };
    router.events.on('routeChangeStart', onRouteChangeStart);
    router.events.on('routeChangeComplete', onRouteChangeComplete);
    window.addEventListener('popstate', onPopState);
    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
      router.events.off('routeChangeComplete', onRouteChangeComplete);
      window.removeEventListener('popstate', onPopState);
    };
  }, [router.events]);
  useEffect(() => {
    // Skip URL modification on mobile devices to prevent issues with native link copying/sharing
    if (isMobileDevice()) {
      return;
    }
    const currentUrl = window.location.href;
    const modifiedUrl = prepareUrlToShare(currentUrl);
    if (modifiedUrl !== currentUrl) {
      try {
        window.history.replaceState({}, '', modifiedUrl);
        if (document.title) {
          document.title = document.title + ' ';
        }
      } catch (error) {
        console.warn('History replaceState failed:', error);
      }
    }
  }, [router.asPath]);
  const getPackshot = () => pageProps?.metaData?.image ? pageProps?.metaData?.image : null;
  const overWriteDefaultSeoConfig = () => {
    const packshot = getPackshot();
    if (!packshot) return DefaultSeoConfig();
    const defaultSeoConfig = DefaultSeoConfig();
    defaultSeoConfig.additionalLinkTags.push({
      rel: 'preload',
      href: packshot,
      as: 'image',
      type: 'image/jpeg',
      crossOrigin: ''
    });
    return defaultSeoConfig;
  };
  const appContent = <>
      <Head>
        <meta name="viewport" content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no" />
      </Head>
      <ReaffiliatesHead />
      <Script strategy="beforeInteractive" src={`${BASE_PATH}/js/properties.js`} />
      <Script strategy="beforeInteractive" src={`${BASE_PATH}/js/getPublicEnv.js`} />
      <DefaultSeo {...overWriteDefaultSeoConfig()} />
      <SessionProvider session={pageProps.session} basePath={`${BASE_PATH}/api/auth`}>
        <ThemeProvider theme={theme}>
          <GlobalStyles styles />
          <ErrorBoundary>{getLayout(<Component {...pageProps} />)}</ErrorBoundary>
          <div id="modal-root" />
          {isDevToolbarActive() && <DevToolbar pageProps={pageProps} />}
        </ThemeProvider>
      </SessionProvider>
    </>;

  // Profiler callback - logs render performance
  const onRenderProfiler = (id: string, phase: 'mount' | 'update', actualDuration: number, baseDuration: number, startTime: number, commitTime: number) => {
    if (process.env.NODE_ENV !== 'production') {
      logger.info({
        id,
        phase,
        actualDuration,
        baseDuration,
        startTime,
        commitTime
      });
    }
  };
  return REACT_PROFILING ?
  // @ts-expect-error - React 19 type compatibility issue with Profiler
  <Profiler id="App" onRender={onRenderProfiler}>
      {appContent}
    </Profiler> : appContent;
};
export function reportWebVitals(metric: {
  id: string;
  name: string;
  value: number;
  label: string;
}) {
  if (process.env.NODE_ENV !== 'production') logger.info(metric);
}

// serializeConfig: false — HttpBackend w next-i18next.config (backends nie serializowalne)
export default appWithTranslation(App, nextI18NextConfig);